import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { 
    Shield, 
    Scale, 
    Users, 
    Clock,
    CheckCircle,
    ExternalLink,
    Zap,
    Lock,
    Code,
    Globe,
    Timer,
    Gavel,
    AlertTriangle,
    RefreshCw,
    ArrowRight
} from 'lucide-react';
import { CONTRACT_ADDRESS_V2, UMA_ORACLE_ADDRESSES, WETH_ADDRESSES, getExplorerUrl, SUPPORTED_CHAINS } from '../lib/contract';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function OraclePage() {
    const { t } = useTranslation();
    const [activeAssertions, setActiveAssertions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const deployments = [
        { chain: 'Base', chainId: 8453, explorer: 'https://basescan.org', symbol: 'ETH' },
        { chain: 'Arbitrum', chainId: 42161, explorer: 'https://arbiscan.io', symbol: 'ETH' },
        { chain: 'Polygon', chainId: 137, explorer: 'https://polygonscan.com', symbol: 'MATIC' },
    ];

    // Fetch active assertions (bets in assertion state)
    const fetchActiveAssertions = async () => {
        try {
            const response = await fetch(`${API_URL}/api/onchain/bets?status=in_assertion`);
            if (response.ok) {
                const data = await response.json();
                setActiveAssertions(data);
            }
        } catch (error) {
            console.error('Failed to fetch assertions:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchActiveAssertions();
        // Refresh every 30 seconds
        const interval = setInterval(fetchActiveAssertions, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchActiveAssertions();
    };

    // Calculate time remaining for assertion
    const getTimeRemaining = (assertionTime) => {
        if (!assertionTime) return null;
        const assertedAt = new Date(assertionTime);
        const expiresAt = new Date(assertedAt.getTime() + 2 * 60 * 60 * 1000); // 2 hours
        const now = new Date();
        const remaining = expiresAt - now;
        
        if (remaining <= 0) return { expired: true };
        
        const hours = Math.floor(remaining / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const progress = (remaining / (2 * 60 * 60 * 1000)) * 100;
        
        return { hours, minutes, progress, expired: false };
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="oracle-page">
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-xl bg-[#00FF66]/20 flex items-center justify-center">
                        <Scale className="w-6 h-6 text-[#00FF66]" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-white">
                            <span className="text-[#00FF66]">UMA</span> {t('oraclePage.headerTitle')}
                        </h1>
                        <p className="text-white/50">{t('oraclePage.headerSubtitle')}</p>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Badge className="bg-[#00FF66]/20 text-[#00FF66] border-[#00FF66]/30">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {t('oraclePage.v2Live')}
                    </Badge>
                    <Badge className="bg-[#00FF66]/20 text-[#00FF66] border-[#00FF66]/30">
                        <Globe className="w-3 h-3 mr-1" />
                        {t('oraclePage.chainsDeployed')}
                    </Badge>
                    <Badge className="bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/30">
                        <Shield className="w-3 h-3 mr-1" />
                        {t('oraclePage.oracleEnabled')}
                    </Badge>
                </div>
            </div>

            {/* Status Banner */}
            <Alert className="mb-8 border-[#00FF66]/30 bg-[#00FF66]/10">
                <CheckCircle className="w-4 h-4 text-[#00FF66]" />
                <AlertTitle className="text-[#00FF66]">{t('oraclePage.v2DeployedTitle')}</AlertTitle>
                <AlertDescription className="text-white/60">
                    {t('oraclePage.v2DeployedDesc')}
                </AlertDescription>
            </Alert>

            {/* Active Assertions Section */}
            <Card className="bg-white/[0.02] border-white/5 mb-8">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="text-xl text-white flex items-center gap-2">
                            <Timer className="w-5 h-5 text-[#FFD700]" />
                            {t('oraclePage.activeAssertions')}
                        </CardTitle>
                        <CardDescription className="text-white/50">
                            {t('oraclePage.challengeWindowDesc')}
                        </CardDescription>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="border-white/10 text-white/70 hover:text-white"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                        {t('oraclePage.refresh')}
                    </Button>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-4">
                            <Skeleton className="h-24 w-full bg-white/10" />
                            <Skeleton className="h-24 w-full bg-white/10" />
                        </div>
                    ) : activeAssertions.length === 0 ? (
                        <div className="text-center py-8">
                            <Scale className="w-12 h-12 text-white/20 mx-auto mb-4" />
                            <p className="text-white/50">{t('oraclePage.noActiveAssertions')}</p>
                            <p className="text-white/30 text-sm mt-1">
                                {t('oraclePage.noAssertionsDesc')}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {activeAssertions.map((bet) => {
                                const timeRemaining = getTimeRemaining(bet.assertion_requested_at);
                                const chainInfo = SUPPORTED_CHAINS[bet.chain_id] || { name: 'Unknown', explorer: '#' };
                                
                                return (
                                    <div 
                                        key={bet.id || bet._id}
                                        className="p-4 rounded-xl bg-white/[0.02] border border-[#FFD700]/20 hover:border-[#FFD700]/40 transition-colors"
                                    >
                                        <div className="flex items-start justify-between gap-4 mb-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Badge className="bg-[#FFD700]/20 text-[#FFD700] text-xs">
                                                        {chainInfo.name}
                                                    </Badge>
                                                    <span className="text-white/40 text-xs">
                                                        {t('oraclePage.betLabel')}{bet.on_chain_id || bet.betId}
                                                    </span>
                                                </div>
                                                <h4 className="text-white font-medium line-clamp-1">
                                                    {bet.description || bet.title}
                                                </h4>
                                            </div>
                                            <Badge className={bet.creator_wins_asserted 
                                                ? 'bg-[#00FF66]/20 text-[#00FF66]' 
                                                : 'bg-[#6F3FCA]/20 text-[#9B6FE8]'
                                            }>
                                                {bet.creator_wins_asserted ? t('oraclePage.creatorWins') : t('oraclePage.takerWins')}
                                            </Badge>
                                        </div>
                                        
                                        {/* Challenge Timer */}
                                        <div className="mb-3">
                                            {timeRemaining?.expired ? (
                                                <div className="flex items-center gap-2 text-[#00FF66]">
                                                    <CheckCircle className="w-4 h-4" />
                                                    <span className="text-sm">{t('oraclePage.challengeEnded')}</span>
                                                </div>
                                            ) : timeRemaining ? (
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between text-sm">
                                                        <div className="flex items-center gap-2 text-[#FFD700]">
                                                            <Clock className="w-4 h-4" />
                                                            <span>{t('oraclePage.challengeWindow')}</span>
                                                        </div>
                                                        <span className="font-mono text-white">
                                                            {timeRemaining.hours}h {timeRemaining.minutes}m {t('oraclePage.remaining')}
                                                        </span>
                                                    </div>
                                                    <Progress value={timeRemaining.progress} className="h-1.5 bg-white/10" />
                                                </div>
                                            ) : (
                                                <div className="text-white/40 text-sm">{t('oraclePage.assertionTimeUnknown')}</div>
                                            )}
                                        </div>
                                        
                                        {/* Assertion ID */}
                                        {bet.assertion_id && (
                                            <div className="flex items-center justify-between text-xs text-white/40 mb-3">
                                                <span>{t('oraclePage.assertionId')}</span>
                                                <span className="font-mono">
                                                    {bet.assertion_id.slice(0, 16)}...
                                                </span>
                                            </div>
                                        )}
                                        
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="text-white/40">{t('oraclePage.pool')}</span>
                                                <span className="text-white font-mono">
                                                    {((bet.creator_stake || 0) + (bet.taker_stake || 0)).toFixed(4)} {chainInfo.name === 'Polygon' ? 'MATIC' : 'ETH'}
                                                </span>
                                            </div>
                                            <Link to={`/bet/${bet.id || bet._id}`}>
                                                <Button size="sm" className="bg-[#FFD700]/20 text-[#FFD700] hover:bg-[#FFD700]/30">
                                                    {t('oraclePage.viewDetails')}
                                                    <ArrowRight className="w-3 h-3 ml-1" />
                                                </Button>
                                            </Link>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Deployment Info */}
            <Card className="bg-white/[0.02] border-white/5 mb-8">
                <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                        <Globe className="w-5 h-5 text-[#00FF66]" />
                        {t('oraclePage.deployedContracts')}
                    </CardTitle>
                    <CardDescription className="text-white/50">
                        {t('oraclePage.sameAddress')} <code className="text-[#00FF66] bg-[#00FF66]/10 px-2 py-1 rounded">{CONTRACT_ADDRESS_V2}</code>
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {deployments.map((deploy) => (
                            <div key={deploy.chainId} className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-white font-semibold">{deploy.chain}</span>
                                    <Badge className="bg-[#00FF66]/20 text-[#00FF66] text-xs">LIVE</Badge>
                                </div>
                                <div className="space-y-2 text-xs text-white/50">
                                    <div className="flex justify-between">
                                        <span>{t('oraclePage.umaOracleLabel')}</span>
                                        <span className="text-white/70 font-mono truncate ml-2" style={{ maxWidth: '120px' }}>
                                            {UMA_ORACLE_ADDRESSES[deploy.chainId]?.slice(0, 10)}...
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>{t('oraclePage.bondCurrency')}</span>
                                        <span className="text-white/70">WETH</span>
                                    </div>
                                </div>
                                <a 
                                    href={`${deploy.explorer}/address/${CONTRACT_ADDRESS_V2}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 flex items-center justify-center gap-1 text-[#00FF66] text-xs hover:underline"
                                >
                                    {t('oraclePage.viewExplorer')} <ExternalLink className="w-3 h-3" />
                                </a>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>

            {/* How it Works */}
            <Card className="bg-white/[0.02] border-white/5 mb-8">
                <CardHeader>
                    <CardTitle className="text-xl text-white">{t('oraclePage.howItWorks')}</CardTitle>
                    <CardDescription className="text-white/50">
                        {t('oraclePage.decentralizedTruth')}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Step 1 */}
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                            <div className="w-10 h-10 rounded-full bg-[#00FF66]/20 flex items-center justify-center mb-4">
                                <span className="text-[#00FF66] font-bold">1</span>
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-2">{t('oraclePage.assertion')}</h3>
                            <p className="text-white/50 text-sm">
                                {t('oraclePage.assertionDescStep')}
                            </p>
                        </div>

                        {/* Step 2 */}
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                            <div className="w-10 h-10 rounded-full bg-[#FFD700]/20 flex items-center justify-center mb-4">
                                <span className="text-[#FFD700] font-bold">2</span>
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-2">{t('oraclePage.challengePeriod')}</h3>
                            <p className="text-white/50 text-sm">
                                {t('oraclePage.challengePeriodDesc')}
                            </p>
                        </div>

                        {/* Step 3 */}
                        <div className="p-6 rounded-xl bg-white/[0.02] border border-white/5">
                            <div className="w-10 h-10 rounded-full bg-[#6F3FCA]/20 flex items-center justify-center mb-4">
                                <span className="text-[#9B6FE8] font-bold">3</span>
                            </div>
                            <h3 className="text-lg font-semibold text-white mb-2">{t('oraclePage.resolutionStep')}</h3>
                            <p className="text-white/50 text-sm">
                                {t('oraclePage.resolutionStepDesc')}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Contract Features */}
            <Card className="bg-white/[0.02] border-white/5 mb-8">
                <CardHeader>
                    <CardTitle className="text-xl text-white flex items-center gap-2">
                        <Code className="w-5 h-5 text-[#00FF66]" />
                        {t('oraclePage.contractFeatures')}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4 text-[#00FF66]" />
                                <span className="text-white font-semibold">requestOracleResolution()</span>
                            </div>
                            <p className="text-sm text-white/50">
                                {t('oraclePage.step1Desc')}
                            </p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4 text-[#00FF66]" />
                                <span className="text-white font-semibold">assertionResolvedCallback()</span>
                            </div>
                            <p className="text-sm text-white/50">
                                {t('oraclePage.step2Desc')}
                            </p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4 text-[#00FF66]" />
                                <span className="text-white font-semibold">{t('oraclePage.twoHourLiveness')}</span>
                            </div>
                            <p className="text-sm text-white/50">
                                {t('oraclePage.step3Desc')}
                            </p>
                        </div>
                        <div className="p-4 rounded-lg bg-white/[0.02] border border-white/5">
                            <div className="flex items-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4 text-[#00FF66]" />
                                <span className="text-white font-semibold">resolveBetManual()</span>
                            </div>
                            <p className="text-sm text-white/50">
                                {t('oraclePage.ownerFallbackDesc')}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Shield className="w-5 h-5 text-[#00FF66]" />
                            {t('oraclePage.trustlessResolution')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-white/60">
                        {t('oraclePage.trustlessDesc')}
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#FFD700]" />
                            {t('oraclePage.communityDriven')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-white/60">
                        {t('oraclePage.communityDesc')}
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Lock className="w-5 h-5 text-[#9B6FE8]" />
                            {t('oraclePage.economicSecurity')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-white/60">
                        {t('oraclePage.economicDesc')}
                    </CardContent>
                </Card>

                <Card className="bg-white/[0.02] border-white/5">
                    <CardHeader>
                        <CardTitle className="text-lg text-white flex items-center gap-2">
                            <Zap className="w-5 h-5 text-[#00FF66]" />
                            {t('oraclePage.fastResolution')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="text-white/60">
                        {t('oraclePage.fastDesc')}
                    </CardContent>
                </Card>
            </div>

            {/* Resolution Options */}
            <Card className="bg-white/[0.02] border-white/5">
                <CardHeader>
                    <CardTitle className="text-xl text-white">{t('oraclePage.resolutionOptions')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex items-start gap-4 p-4 rounded-lg bg-[#00FF66]/5 border border-[#00FF66]/20">
                            <Scale className="w-5 h-5 text-[#00FF66] mt-0.5" />
                            <div>
                                <div className="font-semibold text-white">{t('oraclePage.umaResolutionTitle')}</div>
                                <div className="text-sm text-white/50">
                                    {t('oraclePage.umaResolutionDesc')}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-start gap-4 p-4 rounded-lg bg-white/[0.02] border border-white/5">
                            <CheckCircle className="w-5 h-5 text-[#FFD700] mt-0.5" />
                            <div>
                                <div className="font-semibold text-white">{t('oraclePage.manualResolutionTitle')}</div>
                                <div className="text-sm text-white/50">
                                    {t('oraclePage.manualResolutionDesc')}
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Learn More */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                    href="https://docs.uma.xyz/developers/optimistic-oracle-v3" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#00FF66] text-[#0A0A0C] rounded-lg font-semibold hover:bg-[#00CC52] transition-colors"
                >
                    {t('oraclePage.umaDocs')}
                    <ExternalLink className="w-4 h-4" />
                </a>
                <a 
                    href="https://oracle.uma.xyz" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-colors"
                >
                    {t('oraclePage.umaOracleUI')}
                    <ExternalLink className="w-4 h-4" />
                </a>
                <Link 
                    to="/bets"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-colors"
                >
                    {t('oraclePage.browseActiveBets')}
                </Link>
            </div>
        </div>
    );
}
